import { create } from "zustand";
import { getActiveChatId, setActiveChatId } from "../lib/storage";
import type {
  ChatStreamEvent,
  FinalReportEvent,
  NarrationEvent,
  PlanEvent,
  ProposedEditEvent,
  StepReportEvent,
  ToolGapDetectedEvent,
  VpsPrecheckEvent,
} from "../lib/types";

export type MessageRole = "user" | "assistant";

export interface ChatMessage {
  id: string;
  role: MessageRole;
  text: string;
  streaming?: boolean;
  actions?: Array<{
    name: string;
    args?: Record<string, unknown>;
    result?: string;
    pre?: string;
    post?: string;
    _status?: boolean;
  }>;
  thinking?: string;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  plan: PlanEvent | null;
  stepReports: StepReportEvent[];
  finalReport: FinalReportEvent | null;
  narrations: NarrationEvent[];
  toolGaps: ToolGapDetectedEvent[];
  vpsPrecheck: VpsPrecheckEvent | null;
  proposedEdits: Array<ProposedEditEvent & { state: "pending" | "applied" | "rejected"; error?: string }>;
  status: "idle" | "streaming" | "done" | "error";
  error: string | null;
  updatedAt: number;
  startedAt: number | null;
  finishedAt: number | null;
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/**
 * Удаляет из финального текста backend-разметку, которая нужна была только
 * для OWUI-loader.js / OpenWebUI: HTML <details class="aic-call">,
 * fenced-блоки ```aic-narration```, ```aic-step-report```, ```aic-final-report```,
 * строки-дубли с префиксами 💡/💬/💭/⏳ (token-копии narration).
 *
 * Web-ui рендерит эти блоки через свои компоненты (NarrationLayer, StepReport,
 * FinalReport), поэтому сырой текст в `done.text` от них чистим.
 */
function sanitizeFinalText(raw: string): string {
  let s = String(raw || "");
  // Удаляем aic-* fenced-blocks (блок маркеров OWUI).
  s = s.replace(/```aic-[a-z-]+\n[\s\S]*?\n```/g, "");
  // Удаляем HTML-карточки <details class="aic-...">...</details>.
  s = s.replace(/<details[^>]*class=["'][^"']*aic-[^"']*["'][^>]*>[\s\S]*?<\/details>/g, "");
  // Удаляем строки, начинающиеся с эмодзи-префиксов narration-дублей.
  s = s
    .split("\n")
    .filter((line) => {
      const t = line.trim();
      return !(
        t.startsWith("💡 ") ||
        t.startsWith("💬 ") ||
        t.startsWith("💭 ") ||
        t.startsWith("⏳ ")
      );
    })
    .join("\n");
  // Схлопываем тройные пустые строки.
  s = s.replace(/\n{3,}/g, "\n\n");
  // Дедуп повторяющихся абзацев (>= 80 символов): backend иногда в done.text
  // приклеивает свой буфер поверх уже накопленного. Нормализуем по
  // схлопнутым пробелам/lowercase, оставляем только первое вхождение.
  const paragraphs = s.split(/\n{2,}/);
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const p of paragraphs) {
    const trimmed = p.trim();
    if (!trimmed) continue;
    if (trimmed.length >= 80) {
      const key = trimmed.replace(/\s+/g, " ").toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
    }
    deduped.push(trimmed);
  }
  s = deduped.join("\n\n");
  return s.trim();
}

function emptySession(id?: string, title?: string): ChatSession {
  return {
    id: id || uid(),
    title: title || "Новый чат",
    messages: [],
    plan: null,
    stepReports: [],
    finalReport: null,
    narrations: [],
    toolGaps: [],
    vpsPrecheck: null,
    proposedEdits: [],
    status: "idle",
    error: null,
    updatedAt: Date.now(),
    startedAt: null,
    finishedAt: null,
  };
}

export interface ChatsState {
  sessions: ChatSession[];
  activeId: string;

  // session management
  createChat: (title?: string) => string;
  switchChat: (id: string) => void;
  deleteChat: (id: string) => Promise<void>;
  renameChat: (id: string, title: string) => void;

  // active session helpers
  active: () => ChatSession;
  appendUserMessage: (text: string) => string;
  startAssistant: () => string;
  appendAssistantToken: (id: string, chunk: string) => void;
  finalizeAssistant: (id: string, text: string) => void;
  ingestEvent: (assistantId: string, evt: ChatStreamEvent) => void;

  historyLoaded: boolean;

  // Кросс-девайс синхронизация
  loadFromServer: () => Promise<void>;
  loadChatDetail: (chatId: string) => Promise<void>;

  // Proposed edits (DiffPanel)
  markEditApplied: (edit_id: string) => void;
  markEditRejected: (edit_id: string) => void;
  setEditError: (edit_id: string, error: string) => void;
}

export const useChatsStore = create<ChatsState>((set, get) => {
  const initial = emptySession();
  return {
    sessions: [initial],
    activeId: initial.id,
    historyLoaded: false,

    createChat: (title) => {
      const s = emptySession(undefined, title);
      // Bug fix: persist new chat id в localStorage сразу, иначе loadFromServer
      // (вызываемый по chats_updated во время первого стрима) увидит старый
      // saved chatId и перекинет activeId обратно на предыдущий чат.
      setActiveChatId(s.id);
      set((st) => ({ sessions: [s, ...st.sessions], activeId: s.id }));
      return s.id;
    },

    switchChat: (id) => {
      setActiveChatId(id);
      set({ activeId: id });
      // Если пришли с серверного списка и сообщений нет — подгружаем детали.
      const session = get().sessions.find((s) => s.id === id);
      if (session && session.messages.length === 0) {
        // Не блокируем UI: фоновая загрузка.
        get().loadChatDetail(id).catch(() => {});
      }
    },

    deleteChat: async (id) => {
      // 1. Локально сразу убираем — мгновенный фидбэк UI.
      const st = get();
      const filtered = st.sessions.filter((s) => s.id !== id);
      if (filtered.length === 0) {
        const fresh = emptySession();
        set({ sessions: [fresh], activeId: fresh.id });
      } else {
        const newActive = st.activeId === id ? filtered[0].id : st.activeId;
        set({ sessions: filtered, activeId: newActive });
      }
      // 2. Удаляем на сервере, чтобы при перезагрузке чат не вернулся.
      // Локальные сессии (без серверного id) пропускаем — серверу о них неизвестно.
      try {
        const { deleteRemoteChat } = await import("../lib/api");
        await deleteRemoteChat(id);
      } catch (err) {
        console.warn("[chats] deleteRemoteChat failed:", err);
        // Не возвращаем ошибку — локально уже удалили, серверный кэш догонит при следующей попытке.
      }
    },

    renameChat: (id, title) =>
      set((st) => ({
        sessions: st.sessions.map((s) => (s.id === id ? { ...s, title } : s)),
      })),

    active: () => {
      const st = get();
      return st.sessions.find((s) => s.id === st.activeId) || st.sessions[0];
    },

    appendUserMessage: (text) => {
      const msgId = uid();
      set((st) => ({
        sessions: st.sessions.map((s) => {
          if (s.id !== st.activeId) return s;
          const title = s.messages.length === 0 ? text.slice(0, 40) : s.title;
          return {
            ...s,
            title,
            messages: [...s.messages, { id: msgId, role: "user", text }],
            plan: null,
            stepReports: [],
            finalReport: null,
            narrations: [],
            toolGaps: [],
            vpsPrecheck: null,
            proposedEdits: [],
            error: null,
            status: "streaming" as const,
            updatedAt: Date.now(),
            startedAt: Date.now(),
            finishedAt: null,
          };
        }),
      }));
      return msgId;
    },

    startAssistant: () => {
      const msgId = uid();
      set((st) => ({
        sessions: st.sessions.map((s) => {
          if (s.id !== st.activeId) return s;
          return {
            ...s,
            messages: [...s.messages, { id: msgId, role: "assistant", text: "", streaming: true, actions: [] }],
          };
        }),
      }));
      return msgId;
    },

    appendAssistantToken: (id, chunk) =>
      set((st) => {
        const ownerId = st.sessions.find((x) => x.messages.some((m) => m.id === id))?.id ?? st.activeId;
        return {
        sessions: st.sessions.map((s) => {
          if (s.id !== ownerId) return s;
          // Фильтр служебных токенов от backend (дублей narration / HTML для OWUI):
          // - "💡 Читаю app/main.py..." (pre_action narration token-дубль)
          // - "💬 Файл прочитан, 540 строк" (post_action narration token-дубль)
          // - HTML-карточки <details class="aic-..."> (для OWUI loader.js)
          // Эти текстовые куски приходят параллельно с event_narration / action,
          // поэтому в чате не нужны — UI рендерит их через NarrationLayer.
          const trimmed = (chunk || "").trim();
          if (
            trimmed.startsWith("💡 ") ||
            trimmed.startsWith("💬 ") ||
            trimmed.startsWith("💭 ") ||
            trimmed.startsWith("⏳ ") ||
            trimmed.startsWith("<details")
          ) {
            return s;
          }
          return {
            ...s,
            messages: s.messages.map((m) => {
              if (m.id !== id) return m;
              // Игнорируем чанки уже-законченного сообщения (после Done backend
              // иногда дочинает повторно).
              if (m.streaming === false) return m;
              const current = m.text || "";
              // Substring-дедуп: если длинный chunk целиком уже есть в накопленном
              // тексте — это повторный вызов того же абзаца.
              if (chunk.length >= 60 && current.includes(chunk)) {
                return m;
              }
              return { ...m, text: current + chunk };
            }),
          };
        }),
      };
      }),

    finalizeAssistant: (id, text) => {
      const owner = useChatsStore.getState().sessions.find((x) => x.messages.some((m) => m.id === id))?.id ?? useChatsStore.getState().activeId;
      set((st) => ({
        sessions: st.sessions.map((s) => {
          if (s.id !== owner) return s;
          // Очищаем финальный текст от служебной разметки backend.
          const cleaned = sanitizeFinalText(text);
          // Bug 1 fix: backend иногда присылает в done.text тривиальный fallback "Готово."
          // (когда LLM не сгенерил финальный ответ после серии tool-calls). Если у нас
          // уже накопился полноценный текст из token events — НЕ затираем его на "Готово.".
          const isTrivialFallback =
            cleaned === "" ||
            cleaned === "Готово." ||
            cleaned === "Готово" ||
            cleaned.length < 6;
          return {
            ...s,
            status: "done",
            // Finalize plan: mark all items as done when stream ends
            plan: s.plan ? { ...s.plan, phase: "done" as const, completed: s.plan.total, items: s.plan.items.map((it) => ({ ...it, status: "done" as const })) } : null,
            messages: s.messages.map((m) => {
              if (m.id !== id) return m;
              // accumulated через token-events тоже sanitize: применяем дедуп
              // абзацев, иначе сравнение по длине обманчиво.
              const accumulated = sanitizeFinalText(m.text || "");
              // Если done.text тривиален И есть накопленный текст — оставляем накопленный.
              if (isTrivialFallback && accumulated.length > 5) {
                return { ...m, text: accumulated, streaming: false };
              }
              // Bug 2 fix: backend иногда в done.text шлёт `accumulated + накопленный
              // буфер ещё раз` — длина больше, но это дубль. После sanitizeFinalText
              // повторяющиеся абзацы схлопнуты, поэтому сравниваем уже очищенные.
              // Если cleaned СОДЕРЖИТ accumulated и они различаются только хвостом
              // → cleaned богаче (это нормальный случай).
              // Если accumulated СОДЕРЖИТ cleaned → оставляем accumulated.
              if (accumulated && cleaned && accumulated.includes(cleaned)) {
                return { ...m, text: accumulated, streaming: false };
              }
              if (cleaned.length > accumulated.length) {
                return { ...m, text: cleaned, streaming: false };
              }
              // Иначе сохраняем накопленный (backend мог вернуть короткое summary).
              return { ...m, text: accumulated || (m.text || ""), streaming: false };
            }),
            updatedAt: Date.now(),
            finishedAt: Date.now(),
          };
        }),
      }));
      // Persist actions to localStorage for history (use owner, not active —
      // user could have switched chats while stream was running).
      const st2 = get();
      const active2 = st2.sessions.find((s) => s.id === owner);
      if (active2) {
        const msg = active2.messages.find((m) => m.id === id);
        if (msg?.actions?.length) {
          try {
            const key = `aicores.actions.${active2.id}`;
            // Append to existing actions for this session
            const existing = JSON.parse(localStorage.getItem(key) || "[]");
            const all = [...existing, ...msg.actions.map((a) => ({ name: a.name, args: a.args, result: a.result }))];
            localStorage.setItem(key, JSON.stringify(all));
          } catch { /* quota */ }
        }
      }
    },

    ingestEvent: (assistantId, evt) => {
      const st = get();
      // Stream-events направляются в сессию, где находится сообщение ассистента,
      // а не в текущий активный чат — иначе при переключении вкладок в середине
      // стрима ответ агента «перепрыгивает» в открытый чат.
      const ownerId = st.sessions.find((x) => x.messages.some((m) => m.id === assistantId))?.id ?? st.activeId;
      switch (evt.type) {
        case "plan":
          set((prev) => ({
            sessions: prev.sessions.map((s) =>
              s.id === ownerId ? { ...s, plan: evt } : s,
            ),
          }));
          break;
        case "step_report":
          set({
            sessions: st.sessions.map((s) => {
              if (s.id !== ownerId) return s;
              // Update plan items status based on step_report
              let plan = s.plan;
              if (plan) {
                const idx = (evt as StepReportEvent).index;
                const status = (evt as StepReportEvent).status;
                const items = plan.items.map((it) => {
                  if (it.index === idx) return { ...it, status };
                  if (it.index === idx + 1 && it.status === "pending") return { ...it, status: "active" as const };
                  return it;
                });
                const completed = items.filter((it) => it.status === "done").length;
                plan = { ...plan, items, completed };
              }
              return { ...s, stepReports: [...s.stepReports, evt], plan };
            }),
          });
          break;
        case "final_report":
          set({
            sessions: st.sessions.map((s) =>
              s.id === ownerId ? { ...s, finalReport: evt } : s,
            ),
          });
          break;
        case "narration":
          set({
            sessions: st.sessions.map((s) => {
              if (s.id !== ownerId) return s;
              const narrations = [...s.narrations, evt];
              let messages = s.messages;
              if (evt.kind === "pre_action" || evt.kind === "post_action") {
                messages = messages.map((m) => {
                  if (m.id !== assistantId) return m;
                  const actions = (m.actions || []).slice();
                  if (evt.kind === "pre_action") {
                    actions.push({ name: evt.tool_name || "", pre: evt.text });
                  } else if (actions.length) {
                    const last = actions[actions.length - 1];
                    actions[actions.length - 1] = { ...last, post: evt.text };
                  }
                  return { ...m, actions };
                });
              }
              return { ...s, narrations, messages };
            }),
          });
          break;
        case "tool_gap_detected":
          set({
            sessions: st.sessions.map((s) =>
              s.id === ownerId ? { ...s, toolGaps: [...s.toolGaps, evt] } : s,
            ),
          });
          break;
        case "vps_precheck":
          set({
            sessions: st.sessions.map((s) =>
              s.id === ownerId ? { ...s, vpsPrecheck: evt } : s,
            ),
          });
          break;
        case "chats_updated":
          get().loadFromServer().catch(() => {});
          break;
        case "proposed_edit": {
          const pe = evt as ProposedEditEvent;
          set({
            sessions: st.sessions.map((s) => {
              if (s.id !== ownerId) return s;
              const exists = s.proposedEdits.some((p) => p.edit_id === pe.edit_id);
              if (exists) return s;
              return {
                ...s,
                proposedEdits: [...s.proposedEdits, { ...pe, state: "pending" }],
              };
            }),
          });
          break;
        }
        case "token":
          // Backend в phase=planning шлёт план как обычный token с текстом
          // «**План (N частей)** — [x] 1. ... — [>] 2. ...».
          // Если этот текст добавить в message bubble — пользователь увидит план
          // дважды (один раз в PlanPanel, второй раз в ответе агента). Поэтому
          // skip planning-tokens — они показываются отдельным компонентом.
          if (evt.phase === "planning") break;
          // When a multi-step plan is active, intermediate step text is shown via
          // StepReport cards — don't duplicate it in the message bubble.
          // Only the final done.text will populate the bubble.
          if (st.sessions.find((s) => s.id === ownerId)?.plan) break;
          get().appendAssistantToken(assistantId, evt.text || "");
          break;
        case "status": {
          // Chat-режим (простой) шлёт статусы выполнения tool-ов как {type:"status"}:
          //   "🔎 Ищу в интернете: <запрос>", "📄 Готовлю файл (<kind>)",
          //   "🎨 Генерирую изображение…". Раньше эти события терялись и юзер видел
          //   только мигающую загрузку. Маппим их в actions, чтобы ActionLog показал шаг.
          const stext = (evt as { text?: string }).text || "";
          const sphase = (evt as { phase?: string }).phase;
          if (!stext || (sphase && sphase !== "executing")) break;
          // Имя tool-а (для иконки/лейбла ActionLog) + деталь (для подписи).
          let sname = "";
          let sdetail = "";
          if (stext.includes("🔎") || /ищу в интернете/i.test(stext)) {
            sname = "web_search";
            const m = stext.split(":").slice(1).join(":").trim();
            if (m) sdetail = m;
          } else if (stext.includes("📄") || /готовлю файл/i.test(stext)) {
            sname = "generate_file";
            const m = stext.match(/\(([^)]*)\)/);
            if (m && m[1]) sdetail = m[1];
          } else if (stext.includes("🎨") || /генерирую изображение/i.test(stext)) {
            sname = "generate_image";
          }
          if (!sname) break; // незнакомый статус — не шумим
          set({
            sessions: st.sessions.map((s) => {
              if (s.id !== ownerId) return s;
              return {
                ...s,
                messages: s.messages.map((m) => {
                  if (m.id !== assistantId) return m;
                  const actions = (m.actions || []).slice();
                  // Финализируем предыдущий статус-экшен (спиннер -> галочка),
                  // только если он сам пришёл из status (есть _status маркер).
                  const prev = actions[actions.length - 1];
                  if (prev && prev.result === undefined && (prev as { _status?: boolean })._status) {
                    actions[actions.length - 1] = { ...prev, result: "" };
                  }
                  // Не дублируем тот же самый статус подряд.
                  const tail = actions[actions.length - 1];
                  if (tail && tail.name === sname && (tail.args?.query === sdetail || tail.args?.kind === sdetail) && tail.result === undefined) {
                    return { ...m, actions };
                  }
                  const args: Record<string, unknown> = { _status: true };
                  if (sname === "web_search" && sdetail) args.query = sdetail;
                  if (sname === "generate_file" && sdetail) args.kind = sdetail;
                  actions.push({ name: sname, args, _status: true } as typeof actions[number]);
                  return { ...m, actions };
                }),
              };
            }),
          });
          break;
        }
        case "action": {
          const name = (evt as { name?: string }).name || "";
          const args = (evt as { args?: Record<string, unknown> }).args;
          set({
            sessions: st.sessions.map((s) => {
              if (s.id !== ownerId) return s;
              return {
                ...s,
                messages: s.messages.map((m) => {
                  if (m.id !== assistantId) return m;
                  const actions = (m.actions || []).slice();
                  const last = actions[actions.length - 1];
                  if (last && last.name === name && last.result === undefined && last.pre) {
                    actions[actions.length - 1] = { ...last, args };
                  } else {
                    actions.push({ name, args });
                  }
                  return { ...m, actions };
                }),
              };
            }),
          });
          break;
        }
        case "action_result": {
          const text = (evt as { text?: string }).text || "";
          set({
            sessions: st.sessions.map((s) => {
              if (s.id !== ownerId) return s;
              return {
                ...s,
                messages: s.messages.map((m) => {
                  if (m.id !== assistantId) return m;
                  const actions = (m.actions || []).slice();
                  if (actions.length) {
                    const last = actions[actions.length - 1];
                    actions[actions.length - 1] = { ...last, result: text };
                  }
                  return { ...m, actions };
                }),
              };
            }),
          });
          break;
        }
        case "thinking": {
          const thinkText = (evt as { text?: string }).text || "";
          if (thinkText) {
            set({
              sessions: st.sessions.map((s) => {
                if (s.id !== ownerId) return s;
                return {
                  ...s,
                  messages: s.messages.map((m) => {
                    if (m.id !== assistantId) return m;
                    return { ...m, thinking: (m.thinking || "") + thinkText };
                  }),
                };
              }),
            });
          }
          break;
        }
        case "done":
          get().finalizeAssistant(assistantId, evt.text || "");
          break;
        case "error":
          set({
            sessions: st.sessions.map((s) =>
              s.id === ownerId ? { ...s, status: "error", error: evt.text || "Ошибка" } : s,
            ),
          });
          get().finalizeAssistant(assistantId, evt.text || "");
          break;
        default:
          break;
      }
    },

    loadFromServer: async () => {
      // Подгружает список чатов с backend при логине / возврате на сайт.
      // Сохраняет локальные unsaved-сессии (без серверного id) и мёржит с серверными.
      try {
        const { fetchChats } = await import("../lib/api");
        const remote = await fetchChats();
        if (!remote || remote.length === 0) return;
        set((st) => {
          // Карта существующих локальных сессий по id, чтобы не дублировать.
          const existing = new Map(st.sessions.map((s) => [s.id, s]));
          const merged: ChatSession[] = remote.map((r) => {
            const ex = existing.get(r.id);
            if (ex) return { ...ex, title: r.title, updatedAt: r.updatedAt };
            // Новая (только-что-найденная) сессия — пустые messages,
            // подгружаются при switchChat через loadChatDetail.
            return {
              id: r.id,
              title: r.title || "Чат",
              messages: [],
              plan: null,
              stepReports: [],
              finalReport: null,
              narrations: [],
              toolGaps: [],
              vpsPrecheck: null,
              proposedEdits: [],
              status: "idle" as const,
              error: null,
              updatedAt: r.updatedAt,
              startedAt: null,
              finishedAt: null,
            };
          });
          // Локальные сессии, которых нет на сервере (свежесозданные пустые),
          // оставляем — пользователь может их продолжить.
          const remoteIds = new Set(remote.map((r) => r.id));
          const localOnly = st.sessions.filter((s) => !remoteIds.has(s.id) && s.messages.length > 0);
          const all = [...merged, ...localOnly].sort((a, b) => b.updatedAt - a.updatedAt);
          // Фолбэк: если активная сессия — пустая локальная без сообщений,
          // переключаемся на самую свежую серверную.
          const saved = getActiveChatId();
          const activeStillExists = all.some((s) => s.id === st.activeId);
          const savedExists = saved && all.some((s) => s.id === saved);
          // Bug fix: если активная сейчас — локальная сессия (ещё не сохранена
          // на сервере), в которой пользователь только что писал/получает стрим,
          // НЕ переключаемся на сохранённый saved chatId. Иначе при первом
          // chats_updated событии после нового сообщения юзера выбрасывает в
          // предыдущий чат.
          const currentSession = st.sessions.find((s) => s.id === st.activeId);
          const currentIsLocalActive =
            !!currentSession &&
            !remoteIds.has(currentSession.id) &&
            (currentSession.messages.length > 0 || currentSession.status === "streaming");
          const activeId = currentIsLocalActive
            ? st.activeId
            : savedExists
              ? saved
              : activeStillExists
                ? st.activeId
                : (all[0]?.id || st.activeId);
          setActiveChatId(activeId);
          return { sessions: all.length ? all : st.sessions, activeId };
        });
        // Bug fix: после reload активная сессия указывает на серверный chatId,
        // но messages у неё пустые (список с сервера их не содержит). Раньше
        // loadChatDetail вызывался только из switchChat — пользователь видел
        // пустой чат до клика по нему в сайдбаре. Догружаем детали активной.
        const after = get();
        const active = after.sessions.find((s) => s.id === after.activeId);
        if (active && active.messages.length === 0) {
          after.loadChatDetail(active.id).catch(() => {});
        }
      } catch (err) {
        console.warn("[chats] loadFromServer failed:", err);
      } finally {
        // ВАЖНО: снимаем спиннер ВСЕГДА — даже если список пуст (новый юзер без чатов)
        // или запрос упал. Раньше при remote.length===0 был ранний return, и флаг
        // historyLoaded оставался false навсегда → App.tsx висел на «Загрузка…».
        set({ historyLoaded: true });
      }
    },

    loadChatDetail: async (chatId) => {
      try {
        const { fetchChat } = await import("../lib/api");
        const detail = await fetchChat(chatId);
        set((st) => ({
          sessions: st.sessions.map((s) => {
            if (s.id !== chatId) return s;
            const messages: ChatMessage[] = detail.messages.map((m) => {
              const msg: ChatMessage = {
                id: m.id,
                role: m.role === "user" ? "user" : "assistant",
                text: m.text,
              };
              return msg;
            });
            // Restore actions from localStorage
            try {
              const key = `aicores.actions.${chatId}`;
              const raw = localStorage.getItem(key);
              if (raw) {
                const actions = JSON.parse(raw);
                // Attach to last assistant message
                const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
                if (lastAssistant && actions.length) {
                  lastAssistant.actions = actions;
                }
              }
            } catch { /* */ }
            return {
              ...s,
              title: detail.title || s.title,
              messages,
              updatedAt: detail.updatedAt || s.updatedAt,
            };
          }),
        }));
      } catch (err) {
        console.warn("[chats] loadChatDetail failed:", err);
      }
    },

    markEditApplied: (edit_id) =>
      set((st) => ({
        sessions: st.sessions.map((s) => ({
          ...s,
          proposedEdits: s.proposedEdits.map((p) =>
            p.edit_id === edit_id ? { ...p, state: "applied", error: undefined } : p,
          ),
        })),
      })),

    markEditRejected: (edit_id) =>
      set((st) => ({
        sessions: st.sessions.map((s) => ({
          ...s,
          proposedEdits: s.proposedEdits.map((p) =>
            p.edit_id === edit_id ? { ...p, state: "rejected", error: undefined } : p,
          ),
        })),
      })),

    setEditError: (edit_id, error) =>
      set((st) => ({
        sessions: st.sessions.map((s) => ({
          ...s,
          proposedEdits: s.proposedEdits.map((p) =>
            p.edit_id === edit_id ? { ...p, error } : p,
          ),
        })),
      })),
  };
});
